import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  updateOutreachSchema,
  type OutreachChannel,
  type OutreachMessageResponse,
  type OutreachReason,
  type OutreachStatus,
  type UpdateOutreachInput,
} from '@cumpli/shared';
import { Mail, MessageCircle, Play, Send, TriangleAlert } from 'lucide-react';
import { api } from '../lib/api';
import { DateTimePicker } from '../components/datetime-picker';
import { useOutreach, formatDate, formatRelative } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  FieldError,
  Input,
  Label,
  Pill,
  PageHeader,
  Skeleton,
  cn,
  type Tone,
} from '../components/ui';

type OutreachTab = 'sugeridos' | 'programados' | 'enviados';

const TAB_STATUSES: Record<OutreachTab, OutreachStatus[]> = {
  sugeridos: ['draft'],
  programados: ['scheduled', 'sending'],
  enviados: ['sent', 'failed', 'canceled'],
};

function statusTone(status: OutreachStatus): Tone {
  switch (status) {
    case 'sent':
      return 'green';
    case 'scheduled':
    case 'sending':
      return 'blue';
    case 'failed':
      return 'red';
    default:
      return 'slate';
  }
}

function reasonKey(reason: OutreachReason): string {
  switch (reason) {
    case 'documento_por_vencer':
      return 'reasonDocumentoPorVencer';
    case 'documento_vencido':
      return 'reasonDocumentoVencido';
    default:
      return 'reasonDocumentoSolicitado';
  }
}

function statusKey(status: OutreachStatus): string {
  switch (status) {
    case 'scheduled':
      return 'statusScheduled';
    case 'sending':
      return 'statusSending';
    case 'sent':
      return 'statusSent';
    case 'failed':
      return 'statusFailed';
    case 'canceled':
      return 'statusCanceled';
    default:
      return 'statusDraft';
  }
}

/** Small icon + label chip for each channel a message will send through. */
function ChannelChips({ channels }: { channels: OutreachChannel[] }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5">
      {channels.map((c) => (
        <span
          key={c}
          className="inline-flex items-center gap-1 rounded-full bg-canvas px-2 py-0.5 text-[11px] font-medium text-ink-soft"
        >
          {c === 'email' ? <Mail className="h-3 w-3" strokeWidth={2} /> : <MessageCircle className="h-3 w-3" strokeWidth={2} />}
          {c === 'email' ? t('alcance.channelEmail') : t('alcance.channelWhatsApp')}
        </span>
      ))}
    </div>
  );
}

export default function AlcanceClientes() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState<OutreachTab>('sugeridos');
  const [editing, setEditing] = useState<OutreachMessageResponse | null>(null);
  const [scheduling, setScheduling] = useState<OutreachMessageResponse | null>(null);
  const [confirmSendId, setConfirmSendId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useOutreach();
  const items = data?.items ?? [];

  const buckets = useMemo(() => {
    const out: Record<OutreachTab, OutreachMessageResponse[]> = {
      sugeridos: [],
      programados: [],
      enviados: [],
    };
    for (const m of items) {
      for (const key of Object.keys(TAB_STATUSES) as OutreachTab[]) {
        if (TAB_STATUSES[key].includes(m.status)) out[key].push(m);
      }
    }
    const ts = (iso: string | null) => (iso ? new Date(iso).getTime() : 0);
    // Next-to-send first; sent history most-recent first (API order is created_at desc).
    out.programados.sort((a, b) => ts(a.scheduledAt) - ts(b.scheduledAt));
    out.enviados.sort((a, b) => ts(b.sentAt ?? b.updatedAt) - ts(a.sentAt ?? a.updatedAt));
    return out;
  }, [items]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['alcance'] });

  const generate = useMutation({
    mutationFn: api.generateOutreach,
    onSuccess: () => {
      invalidate();
      toast({ message: t('alcance.generated'), kind: 'success' });
    },
    onError: () => toast({ message: t('alcance.generateError'), kind: 'error' }),
  });

  const send = useMutation({
    mutationFn: (id: string) => api.sendOutreach(id),
    onSuccess: () => {
      invalidate();
      setConfirmSendId(null);
      toast({ message: t('alcance.sent'), kind: 'success' });
    },
    onError: () => {
      setConfirmSendId(null);
      toast({ message: t('alcance.sendError'), kind: 'error' });
    },
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelOutreach(id),
    onSuccess: () => {
      invalidate();
      setConfirmCancelId(null);
      toast({ message: t('alcance.canceled'), kind: 'success' });
    },
    onError: () => {
      setConfirmCancelId(null);
      toast({ message: t('common.genericError'), kind: 'error' });
    },
  });

  const runScheduled = useMutation({
    mutationFn: api.runOutreach,
    onSuccess: (res) => {
      invalidate();
      toast({
        message: `${t('alcance.runScheduledDone')} — ${res.enviados}/${res.revisados}`,
        kind: 'success',
      });
    },
    onError: () => toast({ message: t('common.genericError'), kind: 'error' }),
  });

  const list = buckets[tab];
  const emptyKey =
    tab === 'sugeridos' ? 'emptySugeridos' : tab === 'programados' ? 'emptyProgramados' : 'emptyEnviados';

  const tabs: { key: OutreachTab; labelKey: string }[] = [
    { key: 'sugeridos', labelKey: 'tabSugeridos' },
    { key: 'programados', labelKey: 'tabProgramados' },
    { key: 'enviados', labelKey: 'tabEnviados' },
  ];

  return (
    <div>
      <PageHeader
        title={t('alcance.title')}
        subtitle={t('alcance.subtitle')}
        actions={
          <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
            {t('alcance.generate')}
          </Button>
        }
      />

      {/* segmented tab strip */}
      <div className="mb-4 inline-flex overflow-x-auto rounded-xl border border-line bg-panel p-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tabs.map((tb) => {
          const active = tab === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              aria-pressed={active}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-bold transition-colors',
                active ? 'bg-primary text-white shadow-sm' : 'text-ink-soft hover:bg-canvas hover:text-ink'
              )}
            >
              {t(`alcance.${tb.labelKey}`)}
              <span className="ml-1.5 font-mono text-[11px] font-normal opacity-80">
                {buckets[tb.key].length}
              </span>
            </button>
          );
        })}
      </div>

      {tab === 'programados' && list.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={() => runScheduled.mutate()} disabled={runScheduled.isPending}>
            <Play className="h-4 w-4" strokeWidth={2} />
            {t('alcance.runScheduled')}
          </Button>
        </div>
      )}

      {isLoading ? (
        <Card>
          <div className="grid gap-2.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title={t('common.genericError')}
            action={<Button onClick={() => void refetch()}>{t('alcance.retry')}</Button>}
          />
        </Card>
      ) : list.length === 0 ? (
        <Card>
          <EmptyState icon={<Send className="h-8 w-8" strokeWidth={1.5} />} title={t(`alcance.${emptyKey}`)} />
        </Card>
      ) : (
        <div>
          {/* mobile: stacked cards */}
          <div className="grid gap-2.5 sm:hidden">
            {list.map((m) => (
              <div key={m.id} className="rounded-2xl border border-line bg-panel p-4 shadow-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-ink">{m.clientName}</div>
                    <div className="font-mono text-[11px] text-ink-faint">{m.expedienteRef}</div>
                  </div>
                  <Pill tone={statusTone(m.status)}>{t(`alcance.${statusKey(m.status)}`)}</Pill>
                </div>
                <div className="mt-2 text-sm text-ink-soft">{t(`alcance.${reasonKey(m.reason)}`)}</div>
                <div className="mt-2">
                  <ChannelChips channels={m.channels} />
                </div>
                <div className="mt-2 font-mono text-[11px] text-ink-faint">
                  <WhenCell message={m} />
                </div>
                {m.error && <div className="mt-1.5 text-xs font-semibold text-tone-red">{m.error}</div>}
                <RowActions
                  message={m}
                  onEdit={() => setEditing(m)}
                  onSchedule={() => setScheduling(m)}
                  onSend={() => setConfirmSendId(m.id)}
                  onCancel={() => setConfirmCancelId(m.id)}
                  className="mt-3 flex flex-wrap gap-2"
                />
              </div>
            ))}
          </div>

          {/* desktop: table */}
          <Card className="hidden overflow-x-auto p-0 sm:block">
            <table className="w-full min-w-[54rem] text-sm [&_td]:align-top">
              <thead>
                <tr className="border-b border-line text-left">
                  {[
                    t('alcance.colClient'),
                    t('alcance.colReason'),
                    t('alcance.colChannels'),
                    t('alcance.colStatus'),
                    t('alcance.colDate'),
                    t('common.actions'),
                  ].map((h) => (
                    <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((m) => (
                  <tr key={m.id} className="border-b border-line last:border-0 hover:bg-canvas">
                    <td className="px-5 py-3">
                      <span className="font-bold text-ink">{m.clientName}</span>
                      <div className="font-mono text-[10px] text-ink-faint">{m.expedienteRef}</div>
                    </td>
                    <td className="px-5 py-3 text-ink-soft">{t(`alcance.${reasonKey(m.reason)}`)}</td>
                    <td className="px-5 py-3">
                      <ChannelChips channels={m.channels} />
                    </td>
                    <td className="px-5 py-3">
                      <Pill tone={statusTone(m.status)}>{t(`alcance.${statusKey(m.status)}`)}</Pill>
                      {m.error && <div className="mt-1 max-w-[16rem] text-xs font-semibold text-tone-red">{m.error}</div>}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 font-mono text-[11px] text-ink-faint">
                      <WhenCell message={m} />
                    </td>
                    <td className="px-5 py-3">
                      <RowActions
                        message={m}
                        onEdit={() => setEditing(m)}
                        onSchedule={() => setScheduling(m)}
                        onSend={() => setConfirmSendId(m.id)}
                        onCancel={() => setConfirmCancelId(m.id)}
                        className="flex flex-nowrap gap-2"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {editing && <EditDialog key={editing.id} message={editing} onClose={() => setEditing(null)} />}
      {scheduling && (
        <ScheduleDialog key={scheduling.id} message={scheduling} onClose={() => setScheduling(null)} />
      )}

      <Dialog
        open={!!confirmSendId}
        title={t('alcance.confirmSendTitle')}
        onClose={() => setConfirmSendId(null)}
      >
        <p className="mb-4 text-sm text-ink-soft">{t('alcance.confirmSendBody')}</p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setConfirmSendId(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={send.isPending}
            onClick={() => confirmSendId && send.mutate(confirmSendId)}
          >
            {t('alcance.send')}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={!!confirmCancelId}
        title={t('alcance.cancelConfirmTitle')}
        onClose={() => setConfirmCancelId(null)}
      >
        <p className="mb-4 text-sm text-ink-soft">{t('alcance.cancelConfirmBody')}</p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setConfirmCancelId(null)}>
            {t('alcance.keepMessage')}
          </Button>
          <Button
            variant="destructive"
            disabled={cancel.isPending}
            onClick={() => confirmCancelId && cancel.mutate(confirmCancelId)}
          >
            {t('alcance.cancel')}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * The row's relevant timestamp: sent time for sent, failure/cancel time for
 * failed/canceled, scheduled time otherwise — with a relative-time hint where
 * the moment matters (upcoming sends, recent sends).
 */
function WhenCell({ message: m }: { message: OutreachMessageResponse }) {
  if (m.status === 'sent') {
    return (
      <>
        {formatDate(m.sentAt, '—')}
        <div className="text-[10px] opacity-75">{formatRelative(m.sentAt)}</div>
      </>
    );
  }
  if (m.status === 'failed' || m.status === 'canceled') return <>{formatDate(m.updatedAt, '—')}</>;
  if (m.scheduledAt) {
    const overdue = m.status === 'scheduled' && new Date(m.scheduledAt).getTime() < Date.now();
    return (
      <>
        {formatDate(m.scheduledAt, '—')}
        <div className={cn('text-[10px]', overdue ? 'font-semibold text-tone-red' : 'opacity-75')}>
          {formatRelative(m.scheduledAt)}
        </div>
      </>
    );
  }
  return <>—</>;
}

/** Per-row action buttons, gated by the message's current status. */
function RowActions({
  message,
  onEdit,
  onSchedule,
  onSend,
  onCancel,
  className,
}: {
  message: OutreachMessageResponse;
  onEdit: () => void;
  onSchedule: () => void;
  onSend: () => void;
  onCancel: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const editable = message.status === 'draft' || message.status === 'scheduled';
  const sendable = message.status === 'draft' || message.status === 'scheduled';
  const cancelable = message.status === 'draft' || message.status === 'scheduled';
  // Compact so the full action set fits on one line inside a table row.
  const compact = 'whitespace-nowrap px-3 py-1.5 text-xs';

  if (message.status === 'failed') {
    return (
      <div className={className}>
        <Button variant="outline" className={compact} onClick={onSend}>
          {t('alcance.retry')}
        </Button>
      </div>
    );
  }

  if (message.status === 'sent' || message.status === 'canceled') {
    return null;
  }

  return (
    <div className={className}>
      {editable && (
        <Button variant="outline" className={compact} onClick={onEdit}>
          {t('alcance.edit')}
        </Button>
      )}
      <Button variant="outline" className={compact} onClick={onSchedule}>
        {message.status === 'scheduled' ? t('alcance.reschedule') : t('alcance.schedule')}
      </Button>
      {sendable && (
        <Button variant="outline" className={compact} onClick={onSend}>
          {t('alcance.send')}
        </Button>
      )}
      {cancelable && (
        <Button
          variant="outline"
          className={cn(compact, 'text-tone-red hover:border-tone-red')}
          onClick={onCancel}
        >
          {t('alcance.cancel')}
        </Button>
      )}
    </div>
  );
}

function EditDialog({ message, onClose }: { message: OutreachMessageResponse; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UpdateOutreachInput>({
    resolver: zodResolver(updateOutreachSchema),
    defaultValues: {
      subject: message.subject,
      body: message.body,
      channels: message.channels,
    },
  });

  const subject = watch('subject') ?? message.subject;
  const body = watch('body') ?? message.body;

  const save = useMutation({
    mutationFn: (data: UpdateOutreachInput) => api.updateOutreach(message.id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alcance'] });
      toast({ message: t('alcance.updated'), kind: 'success' });
      onClose();
    },
    onError: () => toast({ message: t('alcance.saveError'), kind: 'error' }),
  });

  return (
    <Dialog open wide title={t('alcance.editTitle')} onClose={onClose}>
      <form onSubmit={handleSubmit((data) => save.mutate(data))} className="space-y-4">
        <div>
          <Label htmlFor="subject">{t('alcance.fieldSubject')}</Label>
          <Input id="subject" {...register('subject')} />
          <FieldError message={errors.subject?.message} />
        </div>
        <div>
          <Label htmlFor="body">{t('alcance.fieldBody')}</Label>
          <textarea
            id="body"
            rows={6}
            {...register('body')}
            className="flex w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <FieldError message={errors.body?.message} />
        </div>
        <fieldset>
          <Label>{t('alcance.fieldChannels')}</Label>
          <div className="flex gap-3">
            {(['email', 'whatsapp'] as const).map((c) => (
              <label
                key={c}
                className="flex flex-1 items-center gap-2 rounded-xl border border-line bg-panel p-3 text-sm transition-colors hover:border-ink-faint"
              >
                <input
                  type="checkbox"
                  value={c}
                  {...register('channels')}
                  className="h-4 w-4 shrink-0 accent-primary"
                />
                {c === 'email' ? t('alcance.channelEmail') : t('alcance.channelWhatsApp')}
              </label>
            ))}
          </div>
          <FieldError message={errors.channels && t('alcance.channelsRequired')} />
        </fieldset>

        {/* live preview */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
              {t('alcance.previewEmail')}
            </div>
            <div className="rounded-xl border border-line bg-canvas p-3 text-xs">
              <div className="mb-1.5 border-b border-line-strong pb-1.5 font-semibold text-ink">{subject}</div>
              <div className="whitespace-pre-wrap text-ink-soft">{body}</div>
            </div>
          </div>
          <div>
            <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
              {t('alcance.previewWhatsApp')}
            </div>
            <div className="rounded-xl bg-[#dcf3d4] p-3">
              <div className="whitespace-pre-wrap rounded-lg rounded-tl-none bg-white px-3 py-2 text-xs text-ink shadow-sm">
                {body}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting || save.isPending}>
            {t('alcance.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Parse an ISO string into a Date, or null when absent/invalid. */
function isoToDate(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ScheduleDialog({ message, onClose }: { message: OutreachMessageResponse; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Date | null>(isoToDate(message.scheduledAt));

  const inPast = selected !== null && selected.getTime() <= Date.now();
  const canSchedule = selected !== null && !inPast;

  const presets: { key: string; at: () => Date }[] = [
    { key: 'presetOneHour', at: () => new Date(Date.now() + 3_600_000) },
    {
      key: 'presetTomorrowMorning',
      at: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
    {
      key: 'presetNextWeek',
      at: () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
  ];

  const save = useMutation({
    mutationFn: (scheduledAt: string | null) => api.updateOutreach(message.id, { scheduledAt }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['alcance'] });
      toast({ message: res.scheduledAt ? t('alcance.scheduled') : t('alcance.updated'), kind: 'success' });
      onClose();
    },
    onError: () => toast({ message: t('alcance.saveError'), kind: 'error' }),
  });

  return (
    <Dialog open title={t('alcance.scheduleTitle')} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSchedule && !save.isPending) save.mutate(selected.toISOString());
        }}
      >
        <p className="mb-4 text-sm text-ink-soft">{t('alcance.scheduleHelp')}</p>
        <div className="mb-3 flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setSelected(p.at())}
              className="rounded-full border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-ink-soft transition-colors hover:border-primary hover:text-ink"
            >
              {t(`alcance.${p.key}`)}
            </button>
          ))}
        </div>
        <div className="mb-4">
          <DateTimePicker value={selected} min={new Date()} onChange={setSelected} />
          {inPast && <FieldError message={t('alcance.schedulePast')} />}
          {canSchedule && (
            <p className="mt-2 text-xs text-ink-soft">
              {t('alcance.scheduleSummary')}{' '}
              <span className="font-semibold text-ink">{formatDate(selected.toISOString(), '')}</span>
              <span className="text-ink-faint"> · {formatRelative(selected.toISOString())}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-3">
          {message.scheduledAt && (
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={() => save.mutate(null)}
            >
              {t('alcance.unschedule')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!canSchedule || save.isPending}>
            {t('alcance.schedule')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
